import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';

import type { ActivationCompletionResult } from './activationProgress.types';

const TRANSLATION_KEYS = {
  activated: 'catalog:order.activationProgress.activated',
  failed: 'catalog:order.activationProgress.failed',
  error: 'catalog:order.activationProgress.error',
  enrolledVehicleCount: 'catalog:enrolledVehicleCount',
  adaptersActivated: 'catalog:order.activationProgress.adaptersActivated',
  adaptersFailed: 'catalog:order.activationProgress.adaptersFailed',
  reenrolled: 'catalog:order.activationProgress.reenrolled',
} as const;

/** Subset of `useToasts` this helper needs. */
export type ActivationToastMethods = {
  showSuccessToast: (message: string) => void;
  showErrorToast: (message: string) => void;
};

/**
 * Emits the platform completion notification for a finished activation.
 *
 * A wholesale failure (nothing activated) always raises an error toast — even
 * for bulk — so a failed request is never silent (the batch never starts, so
 * there is no completion card to fall back on).
 *
 * Otherwise, bulk activations (`total > 1`) do NOT toast: their success /
 * partial-failure outcome is surfaced by the in-place completion card (see
 * ActivationCompletionCard). Single-device activations toast their success or
 * failure directly.
 *
 * Centralized so both the immediate (no-batch / submit-error) path and the
 * polled completion path produce identical notifications.
 */
export const showActivationCompletionToasts = (
  { total, success, failure, failed }: ActivationCompletionResult,
  { showSuccessToast, showErrorToast }: ActivationToastMethods
): void => {
  // Wholesale failure always toasts, regardless of batch size.
  if (failed && success === 0) {
    showErrorToast(translateText(TRANSLATION_KEYS.error));
    return;
  }

  // Bulk success / partial-failure outcomes are surfaced by the global
  // activation progress widget, not a toast.
  if (total > 1) {
    return;
  }

  if (success > 0) {
    showSuccessToast(
      translateText(TRANSLATION_KEYS.activated, { count: success })
    );
  }

  if (failure > 0) {
    showErrorToast(translateText(TRANSLATION_KEYS.failed, { count: failure }));
  }
};

/**
 * Emits the completion toast for an activation that was *only* re-enrollments.
 * Those settle on the billing side with no vendor activation to poll, so the
 * order response is the whole outcome and it is reported the moment it resolves.
 *
 * Success only: a failed billing order is a wholesale failure and already
 * reported through {@link showActivationCompletionToasts} (or, from the modal,
 * the inline error banner). Used for a single re-enrolled device — larger
 * selections report through the completion card instead.
 */
export const showReenrollmentCompletionToast = (
  { count }: { count: number },
  { showSuccessToast }: Pick<ActivationToastMethods, 'showSuccessToast'>
): void => {
  if (count <= 0) return;
  showSuccessToast(translateText(TRANSLATION_KEYS.reenrolled, { count }));
};

/** Combined vehicle-enroll + adapter-activation outcome for an OST mixed enroll. */
export type OstEnrollCompletionResult = {
  /** Vehicles enrolled in the same action (0 for an adapters-only enroll). */
  vehicleCount: number;
  /** Adapters that activated successfully. */
  adapterSuccess: number;
  /** Adapters that failed to activate (or couldn't start, e.g. no batch id). */
  adapterFailure: number;
  /**
   * Adapters that were re-enrolled instead of activated. They settle on the
   * billing side and never get a vendor activation batch, so they are reported
   * as their own clause rather than counted as activated.
   */
  adapterReenrolled?: number;
};

/**
 * Emits a SINGLE combined completion toast for an OST enroll that activated
 * adapters alongside enrolling vehicles (Shop Add-Ons). OST is the only flow
 * that mixes vehicles + adapters in one action, so their statuses are reported
 * together here — e.g. "2 vehicles enrolled, 1 adapter failed to activate" —
 * instead of a separate enroll toast plus a generic activation toast.
 *
 * The toast is an error when any adapter failed (so a failure is never dressed
 * up as a success) and a success otherwise. Per-entity clauses are composed so
 * each type reports its own count/verb.
 */
export const showOstEnrollCompletionToast = (
  {
    vehicleCount,
    adapterSuccess,
    adapterFailure,
    adapterReenrolled = 0,
  }: OstEnrollCompletionResult,
  {
    showSuccessToast,
    showErrorToast,
    formatCount = String,
  }: ActivationToastMethods & {
    /**
     * Locale-grouped integer for `{{formattedCount}}` slots. Callers gate this
     * on `LOCALIZATION_FEATURE_FLAG`; defaulting to `String` keeps legacy
     * digit-string output when the flag is off (or when a non-React caller has
     * no formatter to hand).
     */
    formatCount?: (n: number) => string;
  }
): void => {
  const clauses: string[] = [];
  if (vehicleCount > 0) {
    clauses.push(
      translateText(TRANSLATION_KEYS.enrolledVehicleCount, {
        count: vehicleCount,
        formattedCount: formatCount(vehicleCount),
      })
    );
  }
  if (adapterSuccess > 0) {
    clauses.push(
      translateText(TRANSLATION_KEYS.adaptersActivated, {
        count: adapterSuccess,
      })
    );
  }
  if (adapterReenrolled > 0) {
    clauses.push(
      translateText(TRANSLATION_KEYS.reenrolled, { count: adapterReenrolled })
    );
  }
  if (adapterFailure > 0) {
    clauses.push(
      translateText(TRANSLATION_KEYS.adaptersFailed, { count: adapterFailure })
    );
  }
  if (clauses.length === 0) return;

  const message = clauses.join(', ');
  if (adapterFailure > 0) {
    showErrorToast(message);
  } else {
    showSuccessToast(message);
  }
};
